/**
 * /decision-log — SEO landing page targeting the "decision log" /
 * "decision log template" / "architecture decision record" cluster.
 * Differentiation angle: traditional decision logs (ADRs, Confluence
 * pages, markdown files) atrophy because nothing reads them back and
 * nothing links a decision to what depends on it. Draftlize makes the
 * decision log something an AI agent reads AND writes, with dependents
 * auto-flagging stale when a decision changes.
 *
 * English-only + inline (no i18n), mirroring the comparison pages.
 * Chinese variant lives in DecisionLogZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function DecisionLog() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/decision-log"
      hreflangLocales={['en', 'zh', 'zhtw']}
      documentTitle="Decision Log: The Living Kind That Doesn't Rot · Draftlize"
      documentDescription="Most decision logs and ADRs rot — written once, never read, silently outdated. Draftlize makes a decision log an AI agent reads and writes, where dependent decisions auto-flag stale the moment one changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Decision Logs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Most decision logs rot.<br/>
            <em>Here's the living kind.</em>
          </h1>
          <p className="lede">
            A decision log is supposed to answer one question six months later: <em>"why did we decide this?"</em> Most never do. They get written once, buried in a doc, and quietly go stale while the product moves on. The problem isn't discipline — it's that a static document can't know when a decision it recorded no longer holds. Draftlize makes the decision log something an AI agent reads and writes, where every dependent decision flags itself stale the moment one upstream changes.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">A doc vs <em>a living decision log.</em></h2>
          </div>
          <p>A decision log written in Notion, Confluence, or a markdown ADR is a snapshot. The moment a decision changes, the log is wrong and nobody knows. The fix isn't more discipline — it's a substrate that tracks the dependencies for you.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Doc / Confluence / markdown ADR</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Recording a decision</td><td className="ours yes">Typed card: decision, rationale, alternatives, dependencies</td><td className="partial">A paragraph of prose</td></tr>
            <tr><td>Findable six months later</td><td className="ours yes">Addressable by URL / ID</td><td className="no">Buried, you grep the wiki</td></tr>
            <tr><td>A decision changes — do dependents know?</td><td className="ours yes">They auto-flag stale</td><td className="no">No link; it silently rots</td></tr>
            <tr><td>Can an AI agent use it?</td><td className="ours yes">Native read + write via MCP</td><td className="partial">Reads flat text at best</td></tr>
            <tr><td>Who keeps it consistent</td><td className="ours yes">The substrate tracks it</td><td className="no">Your willpower</td></tr>
            <tr><td>Conflict with an earlier decision</td><td className="ours yes">Surfaced automatically</td><td className="no">Discovered in the next post-mortem</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why decision logs <em>rot.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Written for a reader who never comes back</h3>
            <p>A decision log is written for "future us." But future us is busy, and the log lives in a doc nobody reopens. Architecture decision records have existed since 2011 and have always atrophied for exactly this reason — the audience is human, and humans don't re-read.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">No link between a decision and what depends on it</h3>
            <p>You decide pricing in week 3. In week 7 you change it. The three specs that assumed the old price are now wrong — but nothing connects them to the decision, so nobody updates them until something breaks in a demo.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Maintenance is manual, so entropy wins</h3>
            <p>Every decision log dies the same death: it's perfect on day one and 40% wrong by day ninety. Keeping it current is a chore that competes with shipping, and shipping always wins.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">What a <em>living</em> decision log does.</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The AI agent is the reader who never forgets</h3>
            <p>When the log's primary reader is an agent — one that reads every relevant card before it drafts the next spec — the decision log stops being a graveyard and becomes ambient context. It gets read on every turn, not at the next post-mortem.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one decision, dependents flag stale</h3>
            <p>Each decision is a card with explicit dependencies. Change the pricing decision and every card that depended on it turns stale automatically — the same way a build system invalidates everything downstream of a changed file.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Every decision is addressable</h3>
            <p>A decision is a card with a stable ID, citable from a spec, a thread, or Claude Code via MCP. "Per decision c-04" becomes a real link, not a vague memory — and a <a href="/decision-log-template" style={{ color: 'var(--amber)' }}>decision log template</a> or <a href="/architecture-decision-record" style={{ color: 'var(--amber)' }}>architecture decision record</a> becomes a starter pack you clone, not a blank page you dread.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A decision log is only useful if something reads it back. Make that something an agent that never skips a card.
        </blockquote>
        <cite>The discipline you couldn't sustain by hand, the substrate sustains for you.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a decision log?', a: 'A decision log is a running record of the significant decisions a team makes — what was decided, why, what alternatives were considered, and when. Its job is to answer "why did we decide this?" months later, so the reasoning is not lost and old choices are not re-litigated every sprint.' },
            { q: 'What should a decision log include?', a: 'Each entry should capture the decision itself, the context and problem behind it, the alternatives you weighed, the rationale, the date, and the people or specs that depend on it. That last field is the one most logs omit — and it is exactly what lets you know, later, what breaks when the decision changes.' },
            { q: 'Why do decision logs stop being useful?', a: 'Because a static document cannot know when a decision it recorded stopped being true. It is written once, buried in a doc, and quietly goes stale while the product moves on — and since nothing links a decision to what depends on it, nobody updates the downstream work until something breaks.' },
            { q: 'How does Draftlize keep a decision log current?', a: 'Draftlize makes each decision an addressable card that an AI agent reads and writes. Every dependent card declares what it relies on, so when one decision changes, everything downstream auto-flags stale — the discipline you could not sustain by hand, the substrate sustains for you.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your decision log <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Spin up a project, seed it from your existing docs, and let the agent keep your decisions — and everything that depends on them — current.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="decision-log" />
    </MarketingShell>
  );
}
